/**
 * Master Payment Type Registry
 *
 * SINGLE SOURCE OF TRUTH for all payment type variations across aggregators
 * This file maps the chaotic external world to our clean internal taxonomy
 *
 * Jeff Dean Principle: Handle complexity at the edge, keep the core simple
 */

export interface PaymentTypeMapping {
  id: string;
  canonicalName: string;
  category: 'commission' | 'broker_deduction' | 'referrer_deduction' | 'other_deduction' | 'clawback';
  subcategory: 'upfront' | 'trail' | 'arrears' | 'discharged' | 'other';
  variations: string[];    // All known variations from different aggregators
  isIncome: boolean;       // True = goes to gross, False = deduction
  reportingBehavior: 'gross' | 'adjustment' | 'ignore';
  transformAmount: boolean; // True = convert positive deductions to negative
  description: string;
}

/**
 * MASTER REGISTRY - Add new variations here as we encounter them
 *
 * IMPORTANT: When adding new aggregators, add their payment type variations
 * to the appropriate canonical type's variations array
 */
export const PAYMENT_TYPE_REGISTRY: PaymentTypeMapping[] = [
  // ============================================
  // UPFRONT COMMISSIONS
  // ============================================
  {
    id: 'pt_upfront_commission',
    canonicalName: 'upfront_commission',
    category: 'commission',
    subcategory: 'upfront',
    variations: [
      // LMG / Loan Market
      'Upfront Commission',
      'Upfront Commissions',
      'UPFRONT COMMISSION',
      'UPFRONT COMMISSIONS',
      'upfront_commission',
      'upfront_commissions',

      // Other aggregators
      'Initial Commission',
      'Initial Commissions',
      'Settlement Commission',
      'Settlement Commissions',
      'Origination Commission',
      'Origination Commissions',
      'Origination Fee',
      'New Loan Commission',
      'New Loan Commissions',
      'Establishment Commission',
      'Establishment Commissions',
      'Upfront',
      'UPFRONT',
      'Initial',
      'Settlement'
    ],
    isIncome: true,
    reportingBehavior: 'gross',
    transformAmount: false,
    description: 'Initial commission paid at loan settlement'
  },

  // ============================================
  // TRAIL COMMISSIONS
  // ============================================
  {
    id: 'pt_trail_commission',
    canonicalName: 'trail_commission',
    category: 'commission',
    subcategory: 'trail',
    variations: [
      // LMG / Loan Market
      'Trail Commission',
      'Trail Commissions',
      'TRAIL COMMISSION',
      'TRAIL COMMISSIONS',
      'trail_commission',
      'trail_commissions',

      // Other aggregators
      'Ongoing Commission',
      'Ongoing Commissions',
      'Monthly Commission',
      'Monthly Commissions',
      'Trailing Commission',
      'Trailing Commissions',
      'Residual Commission',
      'Residual Commissions',
      'Trail',
      'TRAIL',
      'Ongoing',
      'Monthly',
      'Residual'
    ],
    isIncome: true,
    reportingBehavior: 'gross',
    transformAmount: false,
    description: 'Ongoing monthly commission'
  },

  // ============================================
  // ARREARS COMMISSIONS
  // ============================================
  {
    id: 'pt_arrears_commission',
    canonicalName: 'arrears_commission',
    category: 'commission',
    subcategory: 'arrears',
    variations: [
      'Arrears Commission',
      'ARREARS COMMISSION',
      'arrears_commission',
      'Arrears',
      'ARREARS',
      'Late Payment Commission',
      'Catch-up Commission'
    ],
    isIncome: true,
    reportingBehavior: 'gross',
    transformAmount: false,
    description: 'Commission on loans in arrears'
  },

  // ============================================
  // DISCHARGED COMMISSIONS
  // ============================================
  {
    id: 'pt_discharged_commission',
    canonicalName: 'discharged_commission',
    category: 'commission',
    subcategory: 'discharged',
    variations: [
      'Discharged Commission',
      'DISCHARGED COMMISSION',
      'discharged_commission',
      'Discharged',
      'DISCHARGED',
      'Payout Commission',
      'Discharge Commission'
    ],
    isIncome: true,
    reportingBehavior: 'gross',
    transformAmount: false,
    description: 'Commission on discharged loans'
  },

  // ============================================
  // BROKER UPFRONT DEDUCTIONS
  // ============================================
  {
    id: 'pt_broker_upfront_deduction',
    canonicalName: 'broker_upfront_deduction',
    category: 'broker_deduction',
    subcategory: 'upfront',
    variations: [
      // LMG / ENTO100
      'Broker Upfront Deductions',
      'Broker Upfront Deduction',
      'BROKER UPFRONT DEDUCTIONS',

      // Other aggregators might use
      'Broker Upfront Share',
      'Broker Initial Share',
      'Upfront Broker Split',
      'Upfront Broker Commission',
      'Broker Settlement Share',
      'Broker Origination Split',
      'Broker Initial Deduction',
      'Broker Upfront',
      'Agent Upfront Share'
    ],
    isIncome: false,
    reportingBehavior: 'adjustment',
    transformAmount: true,  // Convert positive to negative
    description: 'Broker\'s share of upfront commission'
  },

  // ============================================
  // BROKER TRAIL DEDUCTIONS
  // ============================================
  {
    id: 'pt_broker_trail_deduction',
    canonicalName: 'broker_trail_deduction',
    category: 'broker_deduction',
    subcategory: 'trail',
    variations: [
      // LMG / ENTO100
      'Broker Trail Deductions',
      'Broker Trail Deduction',
      'BROKER TRAIL DEDUCTIONS',

      // Other aggregators
      'Broker Trail Share',
      'Broker Ongoing Share',
      'Trail Broker Split',
      'Trail Broker Commission',
      'Broker Monthly Share',
      'Broker Residual Split',
      'Broker Trail',
      'Agent Trail Share',
      'Broker Ongoing Commission'
    ],
    isIncome: false,
    reportingBehavior: 'adjustment',
    transformAmount: true,
    description: 'Broker\'s share of trail commission'
  },

  // ============================================
  // BROKER ARREARS DEDUCTIONS
  // ============================================
  {
    id: 'pt_broker_arrears_deduction',
    canonicalName: 'broker_arrears_deduction',
    category: 'broker_deduction',
    subcategory: 'arrears',
    variations: [
      'Broker Arrears Deductions',
      'Broker Arrears Deduction',
      'BROKER ARREARS DEDUCTIONS',
      'Broker Arrears Share',
      'Broker Arrears Split'
    ],
    isIncome: false,
    reportingBehavior: 'adjustment',
    transformAmount: true,
    description: 'Broker\'s share of arrears commission'
  },

  // ============================================
  // BROKER DISCHARGED DEDUCTIONS
  // ============================================
  {
    id: 'pt_broker_discharged_deduction',
    canonicalName: 'broker_discharged_deduction',
    category: 'broker_deduction',
    subcategory: 'discharged',
    variations: [
      'Broker Discharged Deductions',
      'Broker Discharged Deduction',
      'BROKER DISCHARGED DEDUCTIONS',
      'Broker Discharge Share',
      'Broker Payout Share'
    ],
    isIncome: false,
    reportingBehavior: 'adjustment',
    transformAmount: true,
    description: 'Broker\'s share of discharge commission'
  },

  // ============================================
  // REFERRER UPFRONT DEDUCTIONS
  // ============================================
  {
    id: 'pt_referrer_upfront_deduction',
    canonicalName: 'referrer_upfront_deduction',
    category: 'referrer_deduction',
    subcategory: 'upfront',
    variations: [
      'Referrer Upfront Deductions',
      'Referrer Upfront Deduction',
      'REFERRER UPFRONT DEDUCTIONS',
      'Referrer Initial Share',
      'Referral Upfront Fee',
      'Introducer Upfront Share',
      'Referral Partner Upfront'
    ],
    isIncome: false,
    reportingBehavior: 'adjustment',
    transformAmount: true,
    description: 'Referrer\'s share of upfront commission'
  },

  // ============================================
  // REFERRER TRAIL DEDUCTIONS
  // ============================================
  {
    id: 'pt_referrer_trail_deduction',
    canonicalName: 'referrer_trail_deduction',
    category: 'referrer_deduction',
    subcategory: 'trail',
    variations: [
      'Referrer Trail Deductions',
      'Referrer Trail Deduction',
      'REFERRER TRAIL DEDUCTIONS',
      'Referrer Ongoing Share',
      'Referral Trail Fee',
      'Introducer Trail Share',
      'Referral Partner Trail'
    ],
    isIncome: false,
    reportingBehavior: 'adjustment',
    transformAmount: true,
    description: 'Referrer\'s share of trail commission'
  },

  // ============================================
  // TRAIL DEDUCTIONS (Entossey format)
  // ============================================
  {
    id: 'pt_trail_deduction',
    canonicalName: 'trail_deduction',
    category: 'other_deduction',
    subcategory: 'trail',
    variations: [
      'Trail Deductions',
      'TRAIL Deductions',
      'TRAIL DEDUCTIONS',
      'trail_deductions',
      'trail_deduction'
    ],
    isIncome: false,
    reportingBehavior: 'adjustment',
    transformAmount: true,
    description: 'Generic trail deduction (Entossey format)'
  },

  // ============================================
  // DISCHARGED DEDUCTIONS (Entossey format)
  // ============================================
  {
    id: 'pt_discharged_deduction',
    canonicalName: 'discharged_deduction',
    category: 'other_deduction',
    subcategory: 'discharged',
    variations: [
      'Discharged Deductions',
      'DISCHARGED Deductions',
      'DISCHARGED DEDUCTIONS',
      'discharged_deductions',
      'discharged_deduction'
    ],
    isIncome: false,
    reportingBehavior: 'adjustment',
    transformAmount: true,
    description: 'Generic discharged deduction (Entossey format)'
  },

  // ============================================
  // UPFRONT DEDUCTIONS (Entossey format)
  // ============================================
  {
    id: 'pt_upfront_deduction',
    canonicalName: 'upfront_deduction',
    category: 'other_deduction',
    subcategory: 'upfront',
    variations: [
      'Upfront Deductions',
      'UPFRONT Deductions',
      'UPFRONT DEDUCTIONS',
      'upfront_deductions',
      'upfront_deduction'
    ],
    isIncome: false,
    reportingBehavior: 'adjustment',
    transformAmount: true,
    description: 'Generic upfront deduction (Entossey format)'
  },

  // ============================================
  // CLAWBACKS
  // ============================================
  {
    id: 'pt_clawback',
    canonicalName: 'clawback',
    category: 'clawback',
    subcategory: 'other',
    variations: [
      'Clawback',
      'CLAWBACK',
      'Claw Back',
      'Commission Reversal',
      'Commission Clawback',
      'Refund',
      'Commission Refund',
      'Negative Commission',
      'Commission Recovery'
    ],
    isIncome: false,
    reportingBehavior: 'adjustment',
    transformAmount: false,  // Already negative
    description: 'Commission clawback on early discharge'
  },

  // ============================================
  // ADJUSTMENTS (Commission Corrections)
  // ============================================
  {
    id: 'pt_adjustment',
    canonicalName: 'adjustment',
    category: 'other_deduction',
    subcategory: 'other',
    variations: [
      // Loan Market / AFG
      'Adjustment',
      'ADJUSTMENT',
      'Adjustments',
      'ADJUSTMENTS',
      'adjustment',
      'adjustments',

      // Other aggregators might use
      'Commission Adjustment',
      'Payment Adjustment',
      'Correction',
      'Commission Correction',
      'Payment Correction',
      'Manual Adjustment',
      'Administrative Adjustment',
      'Admin Adjustment',
      'Adj',
      'ADJ'
    ],
    isIncome: false,
    reportingBehavior: 'adjustment',
    transformAmount: false,  // Keep sign as-is (can be + or -)
    description: 'Administrative commission corrections and adjustments (often paired with re-issues, netting to zero)'
  },

  // ============================================
  // OTHER DEDUCTIONS (Not commission-related)
  // ============================================
  {
    id: 'pt_other_deduction',
    canonicalName: 'other_deduction',
    category: 'other_deduction',
    subcategory: 'other',
    variations: [
      'Deductions',
      'DEDUCTIONS',
      'Conference Levy',
      'Levy',
      'Fee',
      'Charge',
      'Admin Fee',
      'Processing Fee',
      'Service Charge',
      'Other Deductions'
    ],
    isIncome: false,
    reportingBehavior: 'ignore',  // Not included in commission calculations
    transformAmount: true,
    description: 'Non-commission deductions and fees'
  }
];

/**
 * Find canonical payment type for a given variation
 */
export function findCanonicalPaymentType(value: string): PaymentTypeMapping | null {
  const normalizedValue = value.trim().toLowerCase();

  for (const mapping of PAYMENT_TYPE_REGISTRY) {
    for (const variation of mapping.variations) {
      if (variation.toLowerCase() === normalizedValue) {
        return mapping;
      }
    }
  }

  return null;
}

/**
 * Fuzzy match payment type with confidence scoring
 */
export function fuzzyMatchPaymentType(value: string): {
  match: PaymentTypeMapping | null;
  confidence: number;
} {
  const normalizedValue = value.trim().toLowerCase();
  let bestMatch: PaymentTypeMapping | null = null;
  let bestScore = 0;

  for (const mapping of PAYMENT_TYPE_REGISTRY) {
    for (const variation of mapping.variations) {
      const score = calculateSimilarity(normalizedValue, variation.toLowerCase());
      if (score > bestScore) {
        bestScore = score;
        bestMatch = mapping;
      }
    }
  }

  return {
    match: bestScore > 0.7 ? bestMatch : null,
    confidence: bestScore
  };
}

/**
 * Simple similarity calculation (Levenshtein-based)
 */
function calculateSimilarity(str1: string, str2: string): number {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;

  if (longer.length === 0) return 1.0;

  const editDistance = levenshteinDistance(longer, shorter);
  return (longer.length - editDistance) / longer.length;
}

function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
}

/**
 * Get all variations for a canonical name
 */
export function getPaymentTypeVariations(canonicalName: string): string[] {
  const mapping = PAYMENT_TYPE_REGISTRY.find(m => m.canonicalName === canonicalName);
  return mapping ? mapping.variations : [];
}

/**
 * Check if a payment type should be transformed to negative
 */
export function shouldTransformToNegative(paymentType: string): boolean {
  const mapping = findCanonicalPaymentType(paymentType);
  return mapping ? mapping.transformAmount : false;
}

/**
 * Get reporting behavior for a payment type
 */
export function getReportingBehavior(paymentType: string): 'gross' | 'adjustment' | 'ignore' {
  const mapping = findCanonicalPaymentType(paymentType);
  return mapping ? mapping.reportingBehavior : 'ignore';
}